/**
 * Money, and the one rule the asking price bug came down to.
 *
 * BLANK IS NOT ZERO.
 *
 * The old form seeded the asking price input with `0`, coerced the field to a
 * number on every keystroke, and wrote the result straight to the record. Three
 * consequences, all of them bugs Dillon hit:
 *
 *   1. Backspacing over the `0` produced `''`, `Number('')` is `0`, and the
 *      zero reappeared under the cursor. The field could not be emptied.
 *   2. A plant he had not priced yet was stored as 0 and rendered "$0" — free,
 *      at a show, in front of a customer.
 *   3. Typing "12.5" passed through the intermediate "12." which coerces to 12,
 *      so the cursor fought the value on the way to a decimal.
 *
 * The fix is a type, not a patch: a price is `number | null`, `null` means NOT
 * PRICED, and the input holds a STRING until save. Parsing happens once, at
 * save, here — never per keystroke, and never in two places that can drift.
 */

/** What the parser decided about a price the user typed. */
export type PriceParse =
  | { ok: true; cents: number | null }
  | { ok: false; reason: 'not_a_number' | 'negative' | 'too_large' }

/*
 * A hundred thousand dollars. Not a business rule — a fat-finger guard, so a
 * stray keypress in a price field cannot book a six-figure sale at a plant show.
 */
const MAX_CENTS = 10_000_000

/**
 * Parse what the user typed into cents.
 *
 * Blank, whitespace, and a lone currency symbol all mean NOT PRICED — `null`.
 * That is the whole point: the field must be emptiable, and empty must survive
 * the round trip instead of collapsing to zero.
 *
 * An explicit `0` typed by a person is honoured as zero: giveaway plants exist,
 * and refusing to record one would be inventing a different bug. Blank and zero
 * are different answers and this function keeps them different.
 */
export function parsePriceInput(raw: string | null | undefined): PriceParse {
  if (raw === null || raw === undefined) return { ok: true, cents: null }

  // Strip currency symbols, thousands separators and spaces before judging.
  const cleaned = String(raw).replace(/[$,\s]/g, '').trim()
  if (cleaned === '') return { ok: true, cents: null }

  // A trailing decimal point is a half-typed number, not a malformed one, but
  // by save time it should be complete. "12." parses as 12.
  if (!/^-?\d*\.?\d*$/.test(cleaned) || cleaned === '.' || cleaned === '-') {
    return { ok: false, reason: 'not_a_number' }
  }

  const value = Number(cleaned)
  if (!Number.isFinite(value)) return { ok: false, reason: 'not_a_number' }
  if (value < 0) return { ok: false, reason: 'negative' }

  // Rounded, not truncated: 0.1 + 0.2 arithmetic should not shave a cent.
  const cents = Math.round(value * 100)
  if (cents > MAX_CENTS) return { ok: false, reason: 'too_large' }
  return { ok: true, cents }
}

/**
 * Render cents for display.
 *
 * `null` is NOT PRICED and renders as a dash, never "$0.00". A plant with no
 * price must look unpriced everywhere it appears — list, tag, packing list —
 * because the one place it renders as free is the place it gets sold for free.
 */
export function formatPrice(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—'
  return `$${(cents / 100).toFixed(2)}`
}

/** Cents back into the string an editable input should hold. Null is blank. */
export function priceToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return ''
  return (cents / 100).toFixed(2)
}

/** True when a price is set and greater than nothing. */
export function isPriced(cents: number | null | undefined): boolean {
  return cents !== null && cents !== undefined
}
